import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";

const NAV = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/expertise", label: "Expertise", testid: "nav-link-expertise" },
  { to: "/experience", label: "Experience", testid: "nav-link-experience" },
  { to: "/insights", label: "Insights", testid: "nav-link-insights" },
  { to: "/about", label: "About", testid: "nav-link-about" },
];

export const Wordmark = ({ light = false }) => (
  <span className="block leading-none" data-testid="brand-wordmark">
    <span className="block text-[1.35rem] font-extrabold tracking-tight text-crimson">
      English
    </span>
    <span
      className={`block text-[1.05rem] font-bold tracking-tight ${
        light ? "text-white" : "text-navy"
      }`}
    >
      For Leaders
    </span>
  </span>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-navy/10 bg-white/95 backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" aria-label="English for Leaders — home" data-testid="header-logo">
          <Wordmark />
        </Link>

        <nav
          className="hidden items-center gap-9 lg:flex"
          aria-label="Primary navigation"
          data-testid="primary-nav"
        >
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              className={({ isActive }) =>
                `relative py-1 text-sm font-semibold tracking-wide transition-colors duration-300 ${
                  isActive ? "text-navy" : "text-charcoal/70 hover:text-navy"
                } after:absolute after:-bottom-0.5 after:left-0 after:h-0.5 after:bg-crimson after:transition-all after:duration-300 ${
                  isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="header-cta-button"
            className="group inline-flex items-center gap-2 rounded-sm bg-crimson px-5 py-2.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-crimson-dark"
          >
            Let's Connect
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="rounded-sm p-2 text-navy transition-colors hover:bg-offwhite lg:hidden"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <nav
          className="border-t border-navy/10 bg-white px-6 pb-6 pt-2 lg:hidden"
          aria-label="Mobile navigation"
          data-testid="mobile-nav"
        >
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`mobile-${item.testid}`}
              className={({ isActive }) =>
                `block border-b border-navy/5 py-3.5 text-base font-semibold ${
                  isActive ? "text-crimson" : "text-navy"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="mobile-header-cta-button"
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-crimson px-5 py-3 text-sm font-bold text-white"
          >
            Let's Connect
            <ArrowRight className="h-4 w-4" />
          </Link>
        </nav>
      )}
    </header>
  );
};
